import { QueryList } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    Column,
    Context,
    DivRenderer,
    GridNode,
    inverseSortOrder,
    Row,
    RowNode,
    Sort,
} from './experimental.grid';
import { GridContent, SelectTemplateFn } from './experimental.ng.grid';
import { CellDataEntry, RowDataEntry } from './reusable-grid.model';

function parsePxToInt(pxString: string): number {
    return parseInt(pxString.split('px').shift());
}

export function getNumTextLines(element: HTMLElement): number {
    if (element.innerText === '') {
        return -1;
    }

    const {
        lineHeight,
        paddingTop,
        paddingBottom,
        height,
        borderTopWidth,
        borderBottomWidth,
    } = getComputedStyle(element);

    const lineHeightParsed = parsePxToInt(lineHeight);
    const borderWidth =
        parsePxToInt(borderTopWidth) + parsePxToInt(borderBottomWidth);
    const paddingWidth = parsePxToInt(paddingTop) + parsePxToInt(paddingBottom);
    const contentHeight = parsePxToInt(height) - borderWidth - paddingWidth;

    return Math.floor(contentHeight / lineHeightParsed);
}

export function getExistingEntry<T extends CellDataEntry | RowDataEntry>(
    entryList: T[],
    rowId: Row['id'],
    colId?: Column['id'],
): CellDataEntry | RowDataEntry | null {
    const existing = entryList.find((entry) => {
        if ('cellIds' in entry) {
            return (
                entry.cellIds.rowId === rowId && entry.cellIds.colId === colId
            );
        } else {
            return (
                entry.rowId === rowId ||
                ('children' in entry &&
                    entry.children?.some((child) => child.id === rowId))
            );
        }
    });

    if (existing == null) {
        return null;
    }

    return existing;
}

export function getHeaderCellEls(renderer: DivRenderer): Element[] {
    return Array.from(
        renderer.contentColumns.columns.children[0].renderContext.element
            .children,
    );
}

function getCellElByColumnId(
    columnId: string,
    cellElList: Element[],
): Element | undefined {
    return cellElList.find(
        (cellEl: HTMLElement) => cellEl.getAttribute('column-id') === columnId,
    );
}

export function getColumnCellEls(
    columnId: string,
    renderer: DivRenderer,
    gridNode: GridNode,
    includeHeaderCellEl: boolean = false,
): Element[] {
    const cellEls = [];

    // Header Cell
    if (includeHeaderCellEl) {
        const headerCellEl = getCellElByColumnId(
            columnId,
            getHeaderCellEls(renderer),
        );

        if (headerCellEl != null) {
            cellEls.push(headerCellEl);
        }
    }

    // Content Cells
    gridNode.children.forEach((rowNode: RowNode) => {
        if (rowNode.renderContext?.element != null) {
            const contentCellEl = getCellElByColumnId(
                columnId,
                Array.from(rowNode.renderContext.element.children),
            );

            if (contentCellEl != null) {
                cellEls.push(contentCellEl);
            }
        }

        if (rowNode.children.length > 0) {
            rowNode.children.forEach((childRow: RowNode) => {
                if (childRow.renderContext?.element != null) {
                    const childCellEl = getCellElByColumnId(
                        columnId,
                        Array.from(childRow.renderContext.element.children),
                    );
                    if (childCellEl != null) {
                        cellEls.push(childCellEl);
                    }
                }
            });
        }
    });

    return cellEls;
}

export async function calculateMinTextWidth(
    cellWrapperEl: HTMLElement,
    elDestroyedSubject: Subject<void>,
    maxNumTextLines = 2,
): Promise<number> {
    return await new Promise<number>((resolve) => {
        const originalWidth = cellWrapperEl.style.width;
        const elWithText = cellWrapperEl.children[0] as HTMLElement;

        const cellText = elWithText.innerText;
        if (cellText === '') {
            console.warn(
                'Cannot calculate the minimum width of a cell without any text: ',
                cellWrapperEl,
            );
            resolve(0);
        }

        if (cellText.length === 1) {
            resolve(10);
        }

        let minWidth = parsePxToInt(originalWidth);
        if (isNaN(minWidth)) {
            minWidth = cellWrapperEl.getBoundingClientRect().width;
        }

        let numTextLines = getNumTextLines(elWithText);

        const resizePass = new Subject();
        const resizeFinish = new Subject();
        const forceStop = merge(resizeFinish, elDestroyedSubject);

        const shouldSizeDown = numTextLines <= maxNumTextLines;
        let isSmallText = false;

        resizePass.pipe(takeUntil(forceStop)).subscribe(() => {
            if (isSmallText) {
                minWidth += 10;
            } else {
                if (shouldSizeDown) {
                    minWidth = Math.max(0, minWidth - 10);
                } else {
                    minWidth += 10;
                }
            }

            cellWrapperEl.style.width = `${minWidth}px`;
            numTextLines = getNumTextLines(elWithText);

            if (!isSmallText) {
                if (!shouldSizeDown && numTextLines === maxNumTextLines) {
                    resizeFinish.next();
                }

                if (shouldSizeDown && numTextLines > maxNumTextLines) {
                    minWidth += 20;
                    resizeFinish.next();
                }
            } else {
                if (numTextLines === 1) {
                    resizeFinish.next();
                }
            }

            if (minWidth === 0) {
                if (!isSmallText) {
                    isSmallText = true;
                    cellWrapperEl.style.wordBreak = 'break-word';
                } else {
                    resizeFinish.next();
                }
            }

            resizePass.next();
        });

        forceStop.subscribe(() => {
            // Set the cell back to original properties before calculations
            cellWrapperEl.style.width = originalWidth;
            cellWrapperEl.style.wordBreak = 'normal';

            resolve(minWidth);
        });

        resizePass.next();
    });
}

export const selectTemplate: SelectTemplateFn = (
    context: Context,
    contents: QueryList<GridContent>,
) => {
    if (context.t === 'grid') {
        return contents.find((e) => e.type === 'grid');
    }
    if (context.t === 'row') {
        return contents.find(
            (e) =>
                e.type === 'row' &&
                e.id ===
                    (context?.ctx == null ? 'header' : context.ctx.templateId),
        );
    }
    if (context.t === 'cell') {
        return contents.find(
            (e) => e.type === 'cell' && e.id === context.ctx.templateId,
        );
    }
};

export function reusableGridSort(
    column: Column,
    currentSortDefs: Sort[],
    defaultSortDefs: Sort[],
): Sort[] {
    const nextSortDefs = [...currentSortDefs];

    const sortDefIndex = currentSortDefs.findIndex(
        (sortDef) => sortDef.columnId === column.id,
    );
    const defaultSortDef = defaultSortDefs.find(
        (defSortDef) => defSortDef.columnId === column.id,
    );

    if (sortDefIndex > -1 && defaultSortDef != null) {
        const activeSortDef = currentSortDefs[0];
        const newSortDef = inverseSortOrder(
            column,
            activeSortDef,
            defaultSortDef.order,
        );

        if (sortDefIndex > 0) {
            nextSortDefs.splice(sortDefIndex, 1);
            nextSortDefs.splice(0, 0, newSortDef);
        } else {
            nextSortDefs.splice(0, 1, newSortDef);
        }
    }

    return nextSortDefs;
}
